import axiosInstance from "../api/axiosInstance";
import { ENDPOINTS, API_URL } from "@/constants/api";

export interface AdminUserDetails {
  userId: number;
  username: string;
  name: string;
  firstName?: string;
  lastName?: string;
  gender?: string;
  kiteCount?: number;
  archivedKiteCount?: number;
  maxheight?: number;
  maxairtime?: number;
  maxdistance?: number;
  maxspeed?: number;
  lbEmailSubscribed?: boolean;
  ispro?: boolean;
  isproios?: boolean;
  isproandroid?: boolean;
  freesubscription?: boolean;
  lbVisible?: boolean;
  liveScope?: string;
  privateAccount?: boolean;
  socialInstagram?: string;
  socialTiktok?: string;
  createdAt?: string;
  followingCount?: number;
  followersCount?: number;
  sessionsCount?: number;
  hiddenSessionsCount?: number;
  nReviews?: number;
  slug?: string;
  preferredSport?: string;
  skillLevel?: string;
  weightInKg?: number;
  homeSpotId?: number;
  homeSpotName?: string;
  dateOfBirth?: string;
  profilePicId?: string;
  hasStravaConnected?: boolean;
  hasGarminConnected?: boolean;
  hasTWGConnected?: boolean;
  hasWatch?: boolean;
  supportModeEnabled?: boolean;
  diagnosticsEnabled?: boolean;
}

export interface DeletedUserResult {
  id: number;
  deletedName: string | null;
  deletedUsername: string | null;
  deletedAt: string | null;
  sessionsCount: number;
}

export const UserService = {
  async findUsers(text: string, accesstoken: string) {
    try {
      const response = await axiosInstance.get(
        `${ENDPOINTS.USERS.SEARCH}&searchval=${encodeURIComponent(
          text
        )}&accesstoken=${accesstoken}`
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  },

  async getUserById(userId: number, accesstoken: string): Promise<AdminUserDetails | null> {
    try {
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.USERS}/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching user details:", error);
      return null;
    }
  },

  getProfileImageUrl(profilePicId: string | undefined): string {
    if (!profilePicId) {
      return "";
    }
    return `${API_URL}/image/profile/${profilePicId}`;
  },

  async getUserDevices(userId: number, accesstoken: string) {
    try {
      const response = await axiosInstance.get(
        ENDPOINTS.ADMIN.USER_DEVICES(userId),
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user devices:", error);
      return [];
    }
  },

  async getUserKites(userId: number, accesstoken: string) {
    try {
      const response = await axiosInstance.get(
        ENDPOINTS.ADMIN.USER_KITES(userId),
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user kites:", error);
      return [];
    }
  },

  async searchDeletedUsers(query: string, accesstoken: string): Promise<DeletedUserResult[]> {
    try {
      const response = await axiosInstance.get(
        `${ENDPOINTS.ADMIN.USERS}/deleted/search?query=${encodeURIComponent(query)}`,
        {
          headers: { Authorization: `Bearer ${accesstoken}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error searching deleted users:", error);
      return [];
    }
  },

  async restoreUser(userId: number, accesstoken: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await axiosInstance.post(
        `${ENDPOINTS.ADMIN.USERS}/${userId}/restore`,
        {},
        {
          headers: { Authorization: `Bearer ${accesstoken}` },
        }
      );
      return { success: true };
    } catch (error: any) {
      const message = error?.response?.data?.error || "Failed to restore user";
      return { success: false, message };
    }
  },

  async assignSession(
    sessionId: number,
    data: {
      targetUserId: number;
      deviceId: number;
      userKiteId?: number;
      remark?: string;
      reason?: string;
    },
    accesstoken: string
  ) {
    const response = await axiosInstance.put(
      ENDPOINTS.ADMIN.ASSIGN_SESSION(sessionId),
      data,
      {
        headers: {
          Authorization: `Bearer ${accesstoken}`,
        },
      }
    );
    return response.data;
  },
};
